/**
 * ChatContext.tsx - Chat State Management Context
 *
 * Provides chat-related state and functionality throughout the application.
 * Manages posts, responses, loading states, and UI interactions.
 * Directly accesses circle data from CircleContext for proper data flow.
 */

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, Dispatch, SetStateAction } from 'react';
import { usePost, useLoadResponses, useLanguageSelectionContext } from "core";
import { mutate } from 'swr';
import { t } from 'node_modules/i18next';
import { useCircleContext } from './CircleContext';
import { fetchTTSConfig, TTSConfig } from '@/api/filterApi';
import { useParams } from 'react-router-dom';

interface ChatContextState {
  postId: string;
  setPostId: (id: string) => void;
  post: any;
  setPost: (post: any) => void;
  latestSearchResults: any;
  setLatestSearchResults: (results: any) => void;
  latestResponseId: string;
  setLatestResponseId: (id: string) => void;
  responses: any[];
  setResponses: (responses: any[]) => void;
  refreshChat: boolean;
  setRefreshChat: (refresh: boolean) => void;
  flashingBubbleId: string | null;
  setFlashingBubbleId: (id: string | null) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  waitingForAIResponseId: string | null;
  setWaitingForAIResponseId: (id: string | null) => void;
  recentKey: number;
  setRecentKey: (key: number) => void;
  title: string;
  setTitle: (title: string) => void;
  loadingText: string;
  setLoadingText: (text: string) => void;
  loadingDots: string;
  setLoadingDots: (dots: string) => void;
  loadingDotsNumber: number;
  setLoadingDotsNumber: (number: number) => void;
  placeholder: string;
  setPlaceholder: (placeholder: string) => void;
  responding: "true" | "false";
  setResponding: (responding: "true" | "false") => void;
  // SSE streaming state for in-flight AI responses
  streamingAssistantPostId: string | null;
  setStreamingAssistantPostId: (id: string | null) => void;
  streamingText: string;
  setStreamingText: Dispatch<SetStateAction<string>>;
  streamingVectorSearchResults: any[] | null;
  setStreamingVectorSearchResults: (results: any[] | null) => void;
  // Flag set to true when SSE onComplete fires (streaming text is done)
  streamingComplete: boolean;
  setStreamingComplete: (complete: boolean) => void;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  scrollToBottom: () => void;
  latestPost: any;
  responseResponses: any;
  parentCircleId: string;
  circleName: string;
  circleImageUrl: string;
  ttsConfig: TTSConfig | null;
  autoPlayAudio: boolean;
  setAutoPlayAudio: (autoPlay: boolean) => void;
  pendingQuestion: string | null;
  setPendingQuestion: (question: string | null) => void;
}

const ChatContext = createContext<ChatContextState | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const circleContext = useCircleContext();
  const { language } = useLanguageSelectionContext();

  const [postId, setPostId] = useState<string>("");
  const [post, setPost] = useState<any>(null);
  const [cachedPost, setCachedPost] = useState<any>(null);
  const [latestSearchResults, setLatestSearchResults] = useState<any>(null);
  const [latestResponseId, setLatestResponseId] = useState<string>("");
  const [responses, setResponses] = useState<any[]>([]);
  const [refreshChat, setRefreshChat] = useState<boolean>(false);
  const [flashingBubbleId, setFlashingBubbleId] = useState<string | null>(null);
  const [loading, setLoadingInternal] = useState<boolean>(false);

  const [recentKey, setRecentKey] = useState<number>(0);
  const [title, setTitle] = useState<string>('');
  const [loadingText, setLoadingText] = useState<string>('');
  const [loadingDots, setLoadingDots] = useState<string>(".");
  const [loadingDotsNumber, setLoadingDotsNumber] = useState<number>(1);
  const [placeholder, setPlaceholder] = useState<string>(t("placeholder"));
  const [responding, setResponding] = useState<"true" | "false">("false");
  const [waitingForAIResponseId, setWaitingForAIResponseId] = useState<string | null>(null);
  const [streamingAssistantPostId, setStreamingAssistantPostId] = useState<string | null>(null);
  const [streamingText, setStreamingText] = useState<string>("");
  const [streamingVectorSearchResults, setStreamingVectorSearchResults] = useState<any[] | null>(null);
  const [streamingComplete, setStreamingComplete] = useState<boolean>(false);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const previousPostIdRef = useRef<string>("");
  const [ttsConfig, setTtsConfig] = useState<TTSConfig | null>(null);
  
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);

  // Initialize autoPlayAudio from localStorage
  const [autoPlayAudio, setAutoPlayAudioState] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('autoPlayAudio');
      return stored === 'true';
    }
    return false;
  });

  // Wrapper to persist autoPlayAudio to localStorage
  const setAutoPlayAudio = (value: boolean) => {
    setAutoPlayAudioState(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem('autoPlayAudio', String(value));
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const setLoading = (value: boolean) => {
    setLoadingInternal(value);
};

  // Configure SWR hooks with revalidation on focus/reconnect only
  // Polling is handled by ChatView for the active chat
  const { data: postObj } = usePost(postId, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });
  // Only load responses for latestResponseId if it matches the current postId
  // This prevents loading responses from a previous chat
  const { data: responseResponses } = useLoadResponses(
    latestResponseId && latestResponseId === postId ? latestResponseId : postId, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });
  // Only load latestPost if latestResponseId is a response ID (not the root postId)
  // This prevents showing the wrong post when switching chats
  const shouldLoadLatestPost = latestResponseId && latestResponseId !== postId && postId !== "";
  const { data: latestPostObj } = usePost(
    shouldLoadLatestPost ? latestResponseId : null, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });
  // Only use latestPost if it belongs to the current chat
  // Verify it has a thread that matches current postId
  const latestPost = latestPostObj && latestPostObj.thread && latestPostObj.thread[0] === postId 
    ? latestPostObj 
    : null;

  useEffect(() => {
    // Only update if we have new data or need to clear
    if (postObj && postObj._id === postId) {
      // We have the correct post data
      if (post?._id !== postObj._id) {
        setPost(postObj);
      }
    } else if (!postObj && postId === "") {
      // Clear state for new chat
      if (post !== null) {
        setPost(null);
        setCachedPost(null);
      }
    } else if (!postObj && postId !== "" && cachedPost && cachedPost._id === postId) {
      // Use cached post while loading
      if (post?._id !== cachedPost._id) {
        setPost(cachedPost);
      }
    }
  }, [postObj, postId]);

  useEffect(() => {
    // Cache the current post before switching to a new one
    if (postId && postObj && postObj._id === postId && post && post._id !== postId) {
      setCachedPost(post);
    }
  }, [postId, postObj, post]);

  useEffect(() => {
    scrollToBottom();
  }, [latestResponseId, latestPost]);

  // Fetch TTS configuration - check URL circle first, then fall back to parent
  const { circleId: urlCircleId } = useParams();
  
  useEffect(() => {
    const loadTtsConfig = async () => {
      if (!urlCircleId || ttsConfig) return;

      // First, try to fetch TTS config from the URL circle (the one in the header)
      const urlCircleConfig = await fetchTTSConfig(urlCircleId);
      // Check if the config has options and if they're not empty
      const hasValidOptions = urlCircleConfig && Object.keys(urlCircleConfig).length > 0;
      if (hasValidOptions) {
        setTtsConfig(urlCircleConfig);
      } else {
        // Fall back to parent circle if URL circle config is empty
        const parentCircleIdToUse = circleContext.coreCircleData?.circle?.ancestry?.[0] || circleContext.parentCircleId;
        if (parentCircleIdToUse && parentCircleIdToUse !== urlCircleId) {
          const parentConfig = await fetchTTSConfig(parentCircleIdToUse);
          if (parentConfig && Object.keys(parentConfig).length > 0) {
            setTtsConfig(parentConfig);
          }
        }
      }
    };

    loadTtsConfig();
  }, [urlCircleId, circleContext.parentCircleId, circleContext.coreCircleData?.circle?.ancestry, ttsConfig]);

  useEffect(() => {
    const previousPostId = previousPostIdRef.current;
    
    // If postId changed, invalidate cache for old postId and reset state
    if (previousPostId && previousPostId !== postId) {
      // Invalidate SWR cache for the previous postId to prevent stale data
      mutate([`/post-view-responses/${previousPostId}`, language], undefined, { revalidate: false });
      mutate([`/post-view/${previousPostId}`, language], undefined, { revalidate: false });
      // mutate(`/posts/${previousPostId}`, undefined, { revalidate: false });
      mutate(`/posts/${previousPostId}/responses`, undefined, { revalidate: false });
    }
    
    // Reset chat-specific state when switching chats
    setLatestResponseId(postId);
    // Clear search results from previous chat
    setLatestSearchResults(null);
    // Clear responses array to prevent stale data
    setResponses([]);
    // Clear streaming state
    setStreamingAssistantPostId(null);
    setStreamingText("");
    setStreamingVectorSearchResults(null);
    setStreamingComplete(false);
    // Clear waiting state
    setWaitingForAIResponseId(null);
   
    if (previousPostId && previousPostId !== postId) {
      setLoading(false);
  }
    
    // Update ref for next comparison
    previousPostIdRef.current = postId;
    
    // Force revalidation for new postId if it exists
    if (postId) {
      mutate([`/post-view-responses/${postId}`, language], undefined, { revalidate: true });
      mutate([`/post-view/${postId}`, language], undefined, { revalidate: true });
    }
  }, [postId, language]);

  const value: ChatContextState = {
    postId,
    setPostId,
    post,
    setPost,
    latestSearchResults,
    setLatestSearchResults,
    latestResponseId,
    setLatestResponseId,
    responses,
    setResponses,
    refreshChat,
    setRefreshChat,
    flashingBubbleId,
    setFlashingBubbleId,
    loading,
    setLoading,
    waitingForAIResponseId,
    setWaitingForAIResponseId,
    recentKey,
    setRecentKey,
    title,
    setTitle,
    loadingText,
    setLoadingText,
    loadingDots,
    setLoadingDots,
    loadingDotsNumber,
    setLoadingDotsNumber,
    placeholder,
    setPlaceholder,
    responding,
    setResponding,
    streamingAssistantPostId,
    setStreamingAssistantPostId,
    streamingText,
    setStreamingText,
    streamingVectorSearchResults,
    setStreamingVectorSearchResults,
    streamingComplete,
    setStreamingComplete,
    messagesEndRef,
    scrollToBottom,
    latestPost,
    responseResponses,
    parentCircleId: circleContext.parentCircleId,
    circleName: circleContext.circleName,
    circleImageUrl: circleContext.circleImageUrl,
    ttsConfig,
    autoPlayAudio,
    setAutoPlayAudio,
    pendingQuestion,
    setPendingQuestion,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = (): ChatContextState => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

export const usePostState = () => {
  const { postId, setPostId } = useChatContext();
  return { postId, setPostId };
};

export const useSearchResults = () => {
  const { latestSearchResults, setLatestSearchResults } = useChatContext();
  return { latestSearchResults, setLatestSearchResults };
};

export const useResponseState = () => {
  const { latestResponseId, setLatestResponseId } = useChatContext();
  return { latestResponseId, setLatestResponseId };
};

export const useChatRefresh = () => {
  const {
    refreshChat,
    setRefreshChat,
    flashingBubbleId,
    setFlashingBubbleId
  } = useChatContext();
  return {
    refreshChat,
    setRefreshChat,
    flashingBubbleId,
    setFlashingBubbleId
  };
};

export const useLoadingState = () => {
  const { loading, setLoading } = useChatContext();
  return { loading, setLoading };
};

export const useRecentChatsState = () => {
  const { recentKey, setRecentKey } = useChatContext();
  return { recentKey, setRecentKey };
};

export const useInputState = () => {
  const { title, setTitle } = useChatContext();
  return { title, setTitle };
};

export const useLoadingTextState = () => {
  const {
    loadingText,
    setLoadingText,
    loadingDots,
    setLoadingDots,
    loadingDotsNumber,
    setLoadingDotsNumber
  } = useChatContext();
  return {
    loadingText,
    setLoadingText,
    loadingDots,
    setLoadingDots,
    loadingDotsNumber,
    setLoadingDotsNumber
  };
};

export const usePostData = () => {
  const { post, setPost } = useChatContext();
  return { post, setPost };
};

export const useUIState = () => {
  const { placeholder, setPlaceholder, responding, setResponding } = useChatContext();
  return { placeholder, setPlaceholder, responding, setResponding };
};

export const useResponsesState = () => {
  const { responses, setResponses } = useChatContext();
  return { responses, setResponses };
};

export const useStreamingState = () => {
  const {
    streamingAssistantPostId,
    setStreamingAssistantPostId,
    streamingText,
    setStreamingText,
    streamingVectorSearchResults,
    setStreamingVectorSearchResults,
    streamingComplete,
    setStreamingComplete,
  } = useChatContext();
  return {
    streamingAssistantPostId,
    setStreamingAssistantPostId,
    streamingText,
    setStreamingText,
    streamingVectorSearchResults,
    setStreamingVectorSearchResults,
    streamingComplete,
    setStreamingComplete,
  };
};

export const useChatRefs = () => {
  const { messagesEndRef, scrollToBottom } = useChatContext();
  return { messagesEndRef, scrollToBottom };
};

export const useLatestPostData = () => {
  const { latestPost, responseResponses } = useChatContext();
  return { latestPost, responseResponses };
};

export const useChatCircleData = () => {
  const { parentCircleId, circleName, circleImageUrl } = useChatContext();
  return { parentCircleId, circleName, circleImageUrl };
};

export const useTTSConfig = () => {
  const { ttsConfig } = useChatContext();
  return ttsConfig;
};

export const useAutoPlayAudio = () => {
  const { autoPlayAudio, setAutoPlayAudio } = useChatContext();
  return { autoPlayAudio, setAutoPlayAudio };
};

export const usePendingQuestion = () => {
  const { pendingQuestion, setPendingQuestion } = useChatContext();
  return { pendingQuestion, setPendingQuestion };
};
